import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import formatDigit from "@prospace/prospace-components-library/src/lib-assets/mixins/digitFormatter";

export const useKpi = () => {
    const { t } = useI18n({ useScope: 'global' });
    const addressProgramOrder = ref(null);

    const executionPercentage = (plan, fact, isReverseCalculation) => {
        if (!plan || plan === 0) return 0;
        if (isReverseCalculation && (!fact || fact === 0)) return 0;
        let result = null;

        result = isReverseCalculation? (plan / fact) * 100 : (fact / plan) * 100;

        result = _.round(result, 2);

        return result;
    };

    const roundUpAfter5 = (num) => {
        const intPart = Math.floor(num);
        const fracPart = num - intPart;
        if (fracPart >= 0.5) {
            return Math.ceil(num);
        } else {
            return Math.floor(num);
        }
    };


  const getKpiRow = (
        factPropName,
        planPropName,
        namePropName,
        floatToPercent = false,
        showPlanFactPercentChar = true,
        ceil = false
      ) => {
        let fact = addressProgramOrder.value[factPropName] ?? 0
        let plan = addressProgramOrder.value[planPropName] ?? 0

        if (floatToPercent) {
            fact *= 100
            plan *= 100
        }

        fact = _.floor(fact, 2)
        plan = _.floor(plan, 2)
        
        const percent = executionPercentage(plan, fact, factPropName == 'deltaISAOSAFact')

        if (showPlanFactPercentChar) {
            fact = !ceil ? formatDigit(fact, ',', 2) + '%' : fact + '%'
            plan = !ceil ? formatDigit(plan, ',', 2) + '%' : plan + '%'
        }
        return {
            kpiName: t(namePropName),
            plan: !ceil ? formatDigit(plan, ',', 2) : plan,
            fact: !ceil ? formatDigit(fact, ',', 2) : fact,
            percent: !ceil ? formatDigit(percent, ',', 2) + '%' : percent + '%'
        }
    };

    const kpiBusinessItems = computed(() => {
        if (!addressProgramOrder.value) {
            return [];
        }
        return [
            getKpiRow("osaFact", "osaPlan", "osa", true, true),
            getKpiRow("deltaISAOSAFact", "deltaISAOSAPlan", "deltaIsaOsa", true, true),
            getKpiRow("pogWetWHSFact", "pogWetWHSPlan", "pogWhWet", false, false),
            getKpiRow("pogWetPFTFact", "pogWetPFTPlan", "pogPFWet", false, false),
            getKpiRow("pogWetSHBFact", "pogWetSHBPlan", "pogShWet", false, false),
            getKpiRow("pogDryWHSFact", "pogDryWHSPlan", "pogWhDry", false, false),
            getKpiRow("pogDryPFTFact", "pogDryPFTPlan", "pogPFDry", false, false),
            getKpiRow("lSizeFact", "lSizePlan", "representationMars", false, false),
            getKpiRow("stripeCTDreamFact", "stripeCTDreamPlan", "hangersDreamies", false, false),
            getKpiRow("stripePFTCatFact", "stripePFTCatPlan", "hangersPfCat", false, false),
            getKpiRow("stripePFDogFact", "stripePFDogPlan", "hangersPfDog", false, false),
            getKpiRow("stripeSPFact", "stripeSPPlan", "hangersPouches", false, false),
            getKpiRow("ctShareDreamFact", "ctShareDreamPlan", "brandBlockDreamies", false, false),
            getKpiRow("ctSharePFCatFact", "ctSharePFCatPlan", "brandBlockPfCats", false, false),
            getKpiRow("ctSharePFDogFact", "ctSharePFDogPlan", "brandBlockPfDogs", false, false),
            getKpiRow("exSiteFact", "exSitePlan", "dmpGrocery", false, false),
            getKpiRow("hotZoneFact", "hotZonePlan", "SPTCashRegisterArea", false, false)
        ]
    });

    const kpiBudgetItems = computed(() => {
        if (!addressProgramOrder.value) {
            return [];
        }
        const records = [
          getKpiRow("budgetVisitFact", "budgetVisitPlan", "visits", false, false),
          getKpiRow("budgetOSAFact", "budgetOSAPlan", "osa", false, false),
          getKpiRow("budgetDeltaISAOSAFact", "budgetDeltaISAOSAPlan", "deltaIsaOsa", false, false),
          getKpiRow("budgetPOGWetWHSFact", "budgetPOGWetWHSPlan", "pogWhWet", false, false),
          getKpiRow("budgetPOGWetPFTFact", "budgetPOGWetPFTPlan", "pogPFWet", false, false),
          getKpiRow("budgetPOGWetSHBFact", "budgetPOGWetSHBPlan", "pogShWet", false, false),
          getKpiRow("budgetPOGDryWHSFact", "budgetPOGDryWHSPlan", "pogWhDry", false, false),
          getKpiRow("budgetPOGDryPFTFact", "budgetPOGDryPFTPlan", "pogPFDry", false, false),
          getKpiRow("budgetLSizeFact", "budgetLSizePlan", "representationMars", false, false),
          getKpiRow("budgetStripeCTDreamFact", "budgetStripeCTDreamPlan", "hangersDreamies", false, false),
          getKpiRow("budgetStripePFTCatFact", "budgetStripePFTCatPlan", "hangersPfCat", false, false),
          getKpiRow("budgetStripePFDogFact", "budgetStripePFDogPlan", "hangersPfDog", false, false),
          getKpiRow("budgetStripeSPFact", "budgetStripeSPPlan", "hangersPouches", false, false),
          getKpiRow("budgetCTShareDreamFact", "budgetCTShareDreamPlan", "brandBlockDreamies", false, false),
          getKpiRow("budgetCTSharePFCatFact", "budgetCTSharePFCatPlan", "brandBlockPfCats", false, false),
          getKpiRow("budgetCTSharePFDogFact", "budgetCTSharePFDogPlan", "brandBlockPfDogs", false, false),
          getKpiRow("budgetExSiteFact", "budgetExSitePlan", "dmpGrocery", false, false),
          getKpiRow("budgetHotZoneFact", "budgetHotZonePlan", "SPTCashRegisterArea", false, false),
        ];

        const sum = {
          plan: addressProgramOrder.value.totalSumPlan,
          fact:  addressProgramOrder.value.totalSumFact || 0
        };

        sum.kpiName = t('total');
        sum.percent = executionPercentage(addressProgramOrder.value.totalSumPlan, addressProgramOrder.value.totalSumFact);
        sum.percent = formatDigit(sum.percent, ',', 2) + "%";
        sum.plan = formatDigit(sum.plan, ',', 2);
        sum.fact = formatDigit(sum.fact, ',', 2);
        records.push(sum);

        return records;
    });

    const kpiCoverageItems = computed(() => {
        if (!addressProgramOrder.value) {
            return [];
        }

        return [
            getKpiRow("fteMerchFact", "fteMerchPlan", "fteMerch", false, false),
            getKpiRow(
              "frequencyFact",
              "frequencyPlan",
              "visitSum",
              false,
              false
            ),
        ];
    });

    const kpiTableHeader = computed(() => {
        return [{
            name: t("kpi"),
            value: "kpiName",
            f: (item) => item,
            width: '50%'
        },
        {
            name: t("plan"),
            value: "plan",
            f: (item) => formatDigit(item),
            width: 'calc((50%) / 3 - 10)px'
        },
        {
            name: t("fact"),
            value: "fact",
            f: (item) => formatDigit(item),
            width: 'calc((50%) / 3 - 10)px'
        },
        {
            name: "%",
            value: "percent",
            f: (item) => formatDigit(item),
            width: 'calc((50%) / 3 - 10)px'
        }]
    });

    const setAddressProgramOrder = (value) => {
        addressProgramOrder.value = value;
    };

    return {
        setAddressProgramOrder,
        kpiBusinessItems,
        kpiCoverageItems,
        kpiBudgetItems,
        kpiTableHeader
    }
};
